/**
 * @author Daniel Aguirre
 * @file saga.js
 * @description EducationalElement Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Services
import educationalElementService from '@nephro/services/educational-element-service';

// Constants
import {
  CREATE_EDUCATIONAL_ELEMENT,
  DELETE_EDUCATIONAL_ELEMENT_BY_ID,
  GET_ALL_EDUCATIONAL_ELEMENTS,
  GET_EDUCATIONAL_ELEMENT_BY_ID,
  UPDATE_EDUCATIONAL_ELEMENT,
  GET_EDUCATIONAL_ELEMENT_BY_CATEGORY_ID
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get educationalElements from backend
 * @param { object } action - Action with payload data
 */
export function* getEducationalElementsArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit } = data;
  try {
    const response = yield call(
      educationalElementService.getEducationalElements,
      paged,
      limit,
      page
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get educationalElement info by id
 * @param { object } action - Action with payload data
 */
export function* getEducationalElementById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(
      educationalElementService.getEducationalElementById,
      id
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.educationalElementNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to get educationalElement info by id
 * @param { object } action - Action with payload data
 */
export function* getEducationalElementByCategoryId(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { categoryId } = data;

  try {
    const response = yield call(
      educationalElementService.getEducationalElementByCategoryId,
      categoryId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.educationalElementNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to create educationalElement
 * @param { object } action - Action with payload data
 */
export function* createEducationalElement(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;

  try {
    const response = yield call(
      educationalElementService.createEducationalElement,
      data
    );
    yield put(
      setResponse(
        'success',
        'new educationalElement',
        'educationalMaterial.itemCreated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to update educationalElement
 * @param { object } action - Action with payload data
 */
export function* updateEducationalElement(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(
      educationalElementService.updateEducationalElement,
      data,
      id
    );
    yield put(
      setResponse(
        'success',
        'updated educationalElement',
        'educationalMaterial.itemUpdated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to delete educationalElement by id
 * @param { object } action - Action with payload data
 */
export function* deleteEducationalElementById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(
      educationalElementService.deleteEducationalElementById,
      id
    );
    yield put(
      setResponse(
        'success',
        'deleted educationalElement',
        'educationalMaterial.itemDeleted',
        { ...response },
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        `errors.${err.code}` || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Declare all component sagas
 */
export function* watchEducationalElementSaga() {
  yield takeLatest(GET_ALL_EDUCATIONAL_ELEMENTS, getEducationalElementsArray);
  yield takeLatest(GET_EDUCATIONAL_ELEMENT_BY_ID, getEducationalElementById);
  yield takeLatest(
    GET_EDUCATIONAL_ELEMENT_BY_CATEGORY_ID,
    getEducationalElementByCategoryId
  );
  yield takeLatest(CREATE_EDUCATIONAL_ELEMENT, createEducationalElement);
  yield takeLatest(UPDATE_EDUCATIONAL_ELEMENT, updateEducationalElement);
  yield takeLatest(
    DELETE_EDUCATIONAL_ELEMENT_BY_ID,
    deleteEducationalElementById
  );
}
